/**
 * Works out which therapyRx line each ticked Heilmittel belongs to.
 *
 * A dropdown form edits a ROW: the therapyRx keeps its id, only its term
 * changes, and the appointments hanging off that id come along untouched. A
 * checkbox form has no rows — unticking KG and ticking MT are two independent
 * clicks — so rebuilding the lines from labels alone drops KG's line and mints
 * a fresh id for MT. The backend then refuses the save, because a line with
 * scheduled treatments may not be deleted ("Cannot delete TherapyRx <id>:
 * existing treatments found").
 *
 * This routine puts the missing row identity back: a newly ticked term inherits
 * the id of a term that vanished in the same edit, so the payload looks exactly
 * like the row edit a dropdown form produces.
 *
 * Pairing is deliberately conservative about WHAT may inherit WHAT:
 *  - never across the supplementary boundary — a main Heilmittel becoming an
 *    ergänzendes one is not a replacement, and its appointments must not follow;
 *  - within a parent group first (Heißluft -> Heiße Rolle, both under Wärme),
 *    since those are the same slot on the paper form.
 */

export interface ExistingTherapyLine {
  id: string;
  /** Saved term label — `rawText`, or the resolved prescribedTherapy label. */
  label: string;
}

/**
 * The saved lines, described in the SAME vocabulary as the ticked checkboxes.
 *
 * A line stores whatever spelling put it there, and the catalog accepts many
 * per term: 27 Zahnarzt lines on release and staging hold "PA", the shortLabel
 * of Warmpackung/Fango, where the checkbox is called "Packungen". The form
 * canonicalises on load, so the ticked term is "Packungen" while the raw label
 * stayed "PA" — the diff then matched nothing, the line was left out of the
 * payload, and the server refused the save outright ("Cannot delete TherapyRx
 * <id>: existing treatments found") because appointments hung off it.
 *
 * So both sides go through `canonicalise` — the form's `resolveTherapyTermLabel`,
 * which walks alias -> term -> checkbox spelling. Field order matches the
 * loader's exactly (`rawText || prescribedTherapyId || prescribedTherapy.label`);
 * if the two ever disagree again the same class of bug comes straight back.
 * Unresolvable text is passed through unchanged, so free text still compares
 * against itself.
 */
export const existingTherapyLinesOf = (
  therapyRxs: any[] | null | undefined,
  canonicalise: (label: string) => string
): ExistingTherapyLine[] =>
  (therapyRxs ?? [])
    .map((line: any) => {
      const saved =
        line?.rawText || line?.prescribedTherapyId || line?.prescribedTherapy?.label || "";
      return { id: line?.id, label: saved ? canonicalise(saved) : "" };
    })
    // Placeholder rows (the form pads its table to three) carry no label.
    .filter((line): line is ExistingTherapyLine => !!line.id && !!line.label);

export interface TherapyDiffConfig {
  /** Folds case/spacing so "KG-ZNS" and "kg zns" compare equal. */
  normalize: (term: string) => string;
  isSupplementary: (term: string) => boolean;
  /** Parent group of a term ("Wärme" for "Heißluft"), or null when ungrouped. */
  parentOf: (term: string) => string | null;
}

export interface TherapyReplacement {
  fromId: string;
  fromLabel: string;
  toLabel: string;
}

export interface TherapyDiffResult {
  /** Ticked term -> the therapyRx id to save it under, when one is inherited. */
  idByTerm: Map<string, string>;
  /** Terms that took over a disappearing line, keeping its appointments. */
  replacements: TherapyReplacement[];
  /** Lines nothing inherited — genuinely removed from the prescription. */
  removedLines: ExistingTherapyLine[];
  /** Terms that inherited nothing — genuinely new lines. */
  addedTerms: string[];
}

/** Same parent group scores above a plain same-category pairing. */
const SAME_GROUP_SCORE = 2;
const SAME_CATEGORY_SCORE = 1;

export function diffTherapyLines(
  tickedTerms: string[],
  existingLines: ExistingTherapyLine[],
  config: TherapyDiffConfig
): TherapyDiffResult {
  const { normalize, isSupplementary, parentOf } = config;

  const idByTerm = new Map<string, string>();
  const replacements: TherapyReplacement[] = [];
  const unclaimedLines = existingLines.filter((line) => !!line.label);
  const unmatchedTerms: string[] = [];

  const claim = (index: number): ExistingTherapyLine => unclaimedLines.splice(index, 1)[0];

  // Pass 1: the term is still ticked, so it keeps its own line.
  for (const term of tickedTerms) {
    const index = unclaimedLines.findIndex(
      (line) => normalize(line.label) === normalize(term)
    );
    if (index === -1) {
      unmatchedTerms.push(term);
      continue;
    }
    idByTerm.set(term, claim(index).id);
  }

  // Pass 2: a term that appeared inherits from one that disappeared.
  const addedTerms: string[] = [];
  for (const term of unmatchedTerms) {
    const termIsSupplementary = isSupplementary(term);
    const termParent = parentOf(term);

    let bestIndex = -1;
    let bestScore = 0;

    unclaimedLines.forEach((line, index) => {
      // Hard rule: main and supplementary lines never swap identities.
      if (isSupplementary(line.label) !== termIsSupplementary) return;

      const lineParent = parentOf(line.label);
      const sameGroup =
        !!termParent && !!lineParent && normalize(termParent) === normalize(lineParent);
      const score = sameGroup ? SAME_GROUP_SCORE : SAME_CATEGORY_SCORE;

      // Strictly greater keeps it stable: the earliest best candidate wins.
      if (score > bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    });

    if (bestIndex === -1) {
      addedTerms.push(term);
      continue;
    }

    const inherited = claim(bestIndex);
    idByTerm.set(term, inherited.id);
    replacements.push({
      fromId: inherited.id,
      fromLabel: inherited.label,
      toLabel: term,
    });
  }

  return { idByTerm, replacements, removedLines: unclaimedLines, addedTerms };
}
